import React, { useState, useEffect } from "react";
import useCmsStore from "../../../services/useCmsStore";
import { toast } from "react-toastify";
import { FiX, FiFolder, FiSave } from "react-icons/fi";
import "./Modal.css";

const CategoryModal = ({ isOpen, onClose, category = null }) => {
  const { categories, createCategory, updateCategory } = useCmsStore();
  const isEditing = !!category;

  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    parent_id: null,
    color: "#3B82F6",
    icon: "",
    seo_title: "",
    seo_description: "",
  });

  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || "",
        slug: category.slug || "",
        description: category.description || "",
        parent_id: category.parent_id || null,
        color: category.color || "#3B82F6",
        icon: category.icon || "",
        seo_title: category.seo_title || "",
        seo_description: category.seo_description || "",
      });
    } else {
      setFormData({
        name: "",
        slug: "",
        description: "",
        parent_id: null,
        color: "#3B82F6",
        icon: "",
        seo_title: "",
        seo_description: "",
      });
    }
  }, [category, isOpen]);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      if (field === "name" && !isEditing) {
        newData.slug = generateSlug(value);
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    setIsSaving(true);
    try {
      if (isEditing) {
        await updateCategory(category.id, formData);
        toast.success("Category updated successfully");
      } else {
        await createCategory(formData);
        toast.success("Category created successfully");
      }
      onClose();
    } catch (error) {
      toast.error(error.message || "Failed to save category");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  // Filter out current category and its children for parent selection
  const availableParents = categories.filter((c) => c.id !== category?.id);

  return (
    <div className="cms-modal-overlay" onClick={onClose}>
      <div className="cms-modal cms-modal-md" onClick={(e) => e.stopPropagation()}>
        <div className="cms-modal-header">
          <div className="cms-modal-title">
            <FiFolder size={20} />
            <h3>{isEditing ? "Edit Category" : "New Category"}</h3>
          </div>
          <button className="cms-modal-close" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="cms-modal-body">
            <div className="form-row">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Category name"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => handleChange("slug", e.target.value)}
                  placeholder="category-slug"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Brief description of the category..."
                rows={3}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Parent Category</label>
                <select
                  value={formData.parent_id || ""}
                  onChange={(e) => handleChange("parent_id", e.target.value || null)}
                >
                  <option value="">None (Top Level)</option>
                  {availableParents.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Color</label>
                <div className="color-input-wrapper">
                  <input
                    type="color"
                    value={formData.color}
                    onChange={(e) => handleChange("color", e.target.value)}
                    className="color-input"
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => handleChange("color", e.target.value)}
                    placeholder="#3B82F6"
                    className="color-text"
                  />
                </div>
              </div>
            </div>

            <div className="form-divider">
              <span>SEO Settings</span>
            </div>

            <div className="form-group">
              <label>SEO Title</label>
              <input
                type="text"
                value={formData.seo_title}
                onChange={(e) => handleChange("seo_title", e.target.value)}
                placeholder="SEO title for category page"
                maxLength={70}
              />
            </div>

            <div className="form-group">
              <label>SEO Description</label>
              <textarea
                value={formData.seo_description}
                onChange={(e) => handleChange("seo_description", e.target.value)}
                placeholder="Meta description for category page..."
                rows={2}
                maxLength={160}
              />
            </div>
          </div>

          <div className="cms-modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSaving}>
              <FiSave size={16} />
              <span>{isSaving ? "Saving..." : isEditing ? "Update" : "Create"}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryModal;
